import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Breadcrumb, Button } from 'antd';
import { Link, useNavigate } from 'react-router';

import type { IReview } from '@/api/endpoints/mainPages';
import { useDeleteReviewMutation, useMoveReviewMutation } from '@/api/endpoints/mainPages';
import { DeleteConfirmationModal, DraggableTable } from '@/shared/components';
import { ROUTES, TABLE_TRANSLATIONS } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { CreateReviewModal } from './_components/Modals/CreateReviewModal';
import { EditReviewModal } from './_components/Modals/EditReviewModal';
import { getColumnsData } from './helpers';
import styles from './MainReviewsPage.module.scss';
import type { IReviewItemData } from './types';

interface IProp {
  reviewsData: IReview[] | [];
  areReviewsFetching: boolean;
  pageName: string;
  pageId: number;
}

export const MainReviewsPageComponent = ({ reviewsData, areReviewsFetching, pageName, pageId }: IProp) => {
  const navigate = useNavigate();

  const [deleteReview, { isLoading: isReviewDeleting }] = useDeleteReviewMutation();
  const [moveReview] = useMoveReviewMutation();

  const sendApiRequest = useApiRequest();

  const [isCreateReviewModalOpened, setIsCreateReviewModalOpened] = useState(false);
  const [isEditReviewModalOpened, setIsEditReviewModalOpened] = useState(false);
  const [isDeleteReviewModalOpened, setIsDeleteReviewModalOpened] = useState(false);

  const openCreateReviewModal = useCallback(() => setIsCreateReviewModalOpened(true), []);
  const closeCreateReviewModal = useCallback(() => setIsCreateReviewModalOpened(false), []);

  const openEditReviewModal = useCallback(() => setIsEditReviewModalOpened(true), []);
  const closeEditReviewModal = useCallback(() => setIsEditReviewModalOpened(false), []);

  const openDeleteReviewModal = useCallback(() => setIsDeleteReviewModalOpened(true), []);
  const closeDeleteReviewModal = useCallback(() => setIsDeleteReviewModalOpened(false), []);

  const [tableData, setTableData] = useState<IReview[]>([]);
  const [currentReviewItem, setCurrentReviewItem] = useState<null | IReviewItemData>(null);

  const setItemToOperate = useCallback((data: IReviewItemData | null) => setCurrentReviewItem(data), []);

  const handleWithClearOperateItem = (handleFunction?: VoidFunction) => {
    handleFunction?.();
    setItemToOperate(null);
  };

  const columns = useMemo(() => getColumnsData({ setItemToOperate, openEditReviewModal, openDeleteReviewModal }), []);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!tableData || !over) {
      return;
    }

    if (active.id !== over.id) {
      const activeIndex = tableData.findIndex((i) => i.position === active.id);
      const overIndex = tableData.findIndex((i) => i.position === over.id);

      const reorderedArray = arrayMove(tableData, activeIndex, overIndex);

      setTableData(reorderedArray);

      sendApiRequest({
        successMessage: 'Порядок отзывов успешно изменён',
        apiRequestCallback: () =>
          moveReview({
            mainPageId: pageId,
            reviewId: tableData[activeIndex].id,
            position: Number(overIndex) + 1,
          }),
        errorCallback: () => setTableData(tableData),
      });
    }
  };

  const handleDeleteReview = () => {
    if (currentReviewItem) {
      sendApiRequest({
        successMessage: 'Отзыв успешно удален',
        apiRequestCallback: () =>
          deleteReview({
            mainPageId: pageId,
            reviewId: currentReviewItem.id,
          }),
        successCallback: () => handleWithClearOperateItem(closeDeleteReviewModal),
      });
    }
  };

  useEffect(() => {
    setTableData(reviewsData);
  }, [reviewsData]);

  return (
    <div className={styles.wrapper}>
      <Breadcrumb
        items={[
          { title: 'Публичные страницы' },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}`}>Главная</Link> },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`}>{pageName}</Link> },
          { title: 'Отзывы студентов' },
        ]}
      />
      <Button
        className={styles['create-button']}
        size="large"
        type="primary"
        onClick={openCreateReviewModal}
      >
        Добавить отзыв
      </Button>
      <DraggableTable
        onDragEnd={onDragEnd}
        keysForSorting={tableData.map((i) => i.position)}
        columns={columns}
        rowKey="position"
        size="large"
        locale={TABLE_TRANSLATIONS}
        pagination={false}
        dataSource={tableData}
        loading={areReviewsFetching}
      />
      <Button
        className={styles.button}
        size="large"
        type="dashed"
        onClick={() => navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`)}
      >
        Назад
      </Button>
      {isDeleteReviewModalOpened && (
        <DeleteConfirmationModal
          isModalOpen={isDeleteReviewModalOpened}
          description="Вы уверены, что хотите удалить данный отзыв?"
          title="Удаление отзыва"
          handleCancel={() => handleWithClearOperateItem(closeDeleteReviewModal)}
          handleOk={handleDeleteReview}
          itemToOperate={currentReviewItem}
          shouldDisableElement={isReviewDeleting}
        />
      )}
      {isCreateReviewModalOpened && (
        <CreateReviewModal
          pageId={pageId}
          isModalOpen={isCreateReviewModalOpened}
          handleCancel={() => handleWithClearOperateItem(closeCreateReviewModal)}
        />
      )}
      {isEditReviewModalOpened && currentReviewItem && (
        <EditReviewModal
          pageId={pageId}
          isModalOpen={isEditReviewModalOpened}
          handleCancel={() => handleWithClearOperateItem(closeEditReviewModal)}
          handleClearOperateItem={() => handleWithClearOperateItem()}
          itemToOperate={currentReviewItem}
        />
      )}
    </div>
  );
};
